'use client';

import Link from 'next/link';
import { MarketingNav } from '@/components/marketing/MarketingNav';
import { MarketingFooter } from '@/components/marketing/MarketingFooter';

export default function FeaturesPage() {
  return (
    <>
      <MarketingNav />

      <main className="pt-[56px]">
        {/* Header */}
        <section className="bg-white border-b border-[#eee]">
          <div className="max-w-[1200px] mx-auto px-6 py-16 lg:py-20">
            <h1 className="text-[36px] sm:text-[44px] font-semibold text-[#1a1a1a] leading-[1.1] tracking-[-0.02em] mb-4">
              Features
            </h1>
            <p className="text-[18px] text-[#555] leading-[1.65] max-w-[560px]">
              TekSpert Mail provides a full suite of email tools built for productivity, privacy, and reliability.
            </p>
          </div>
        </section>

        {/* Unified Inbox */}
        <section className="bg-white border-b border-[#eee]">
          <div className="max-w-[1200px] mx-auto px-6 py-16 lg:py-20">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
              <div>
                <h2 className="text-[24px] sm:text-[28px] font-semibold text-[#1a1a1a] leading-tight mb-4">
                  Unified multi-account inbox
                </h2>
                <p className="text-[16px] text-[#555] leading-[1.65] mb-4">
                  Manage all of your TekSpert Mail accounts from a single interface. Whether you have addresses on the tekspertmail.co.uk domain or your own custom domain hosted on our servers, every account appears in the sidebar with its own colour-coded section and folder tree.
                </p>
                <p className="text-[16px] text-[#555] leading-[1.65] mb-4">
                  You can view all accounts at once in a combined inbox, or filter to a specific account to focus on just one mailbox. Each account maintains its own Inbox, Sent, Drafts, Spam, and Trash folders independently.
                </p>
                <p className="text-[16px] text-[#555] leading-[1.65]">
                  Switching between accounts is instant — there is no delay or page reload. Unread counts, folder structures, and sync status are tracked separately per account and updated in real-time.
                </p>
              </div>
              <div>
                <h2 className="text-[24px] sm:text-[28px] font-semibold text-[#1a1a1a] leading-tight mb-4">
                  Rich email composer
                </h2>
                <p className="text-[16px] text-[#555] leading-[1.65] mb-4">
                  The built-in email composer provides a full rich text editor with formatting controls for bold, italic, underline, lists, links, and inline images. You can compose emails in HTML or plain text mode depending on your preference.
                </p>
                <p className="text-[16px] text-[#555] leading-[1.65] mb-4">
                  Attach files of any type directly from your computer. Attached files are uploaded securely and included as standard MIME attachments that are compatible with every email client.
                </p>
                <p className="text-[16px] text-[#555] leading-[1.65]">
                  Set up professional HTML email signatures that are automatically appended to outgoing messages. Each account can have its own distinct signature, so your replies always look appropriate for the context.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Search & Organisation */}
        <section className="bg-[#fafafa] border-b border-[#eee]">
          <div className="max-w-[1200px] mx-auto px-6 py-16 lg:py-20">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
              <div>
                <h2 className="text-[24px] sm:text-[28px] font-semibold text-[#1a1a1a] leading-tight mb-4">
                  Fast search
                </h2>
                <p className="text-[16px] text-[#555] leading-[1.65] mb-4">
                  Search across your entire mailbox — or a specific folder — with results returned in under 100 milliseconds. The search engine indexes sender names, email addresses, subject lines, and message bodies to help you find exactly what you are looking for.
                </p>
                <p className="text-[16px] text-[#555] leading-[1.65]">
                  Advanced search filters let you narrow results by sender, recipient, date range, attachment status, and folder. You can combine multiple filters in a single query to locate specific conversations quickly.
                </p>
              </div>
              <div>
                <h2 className="text-[24px] sm:text-[28px] font-semibold text-[#1a1a1a] leading-tight mb-4">
                  Folders, labels, and organisation
                </h2>
                <p className="text-[16px] text-[#555] leading-[1.65] mb-4">
                  TekSpert Mail supports the standard email folder hierarchy — Inbox, Sent, Drafts, Spam, and Trash — as well as custom folders that you can create and organise to match your workflow. Drag and drop emails between folders to keep your mailbox tidy.
                </p>
                <p className="text-[16px] text-[#555] leading-[1.65]">
                  Star important messages for quick access later. Bulk actions allow you to select multiple emails and mark them as read, move them to a folder, or delete them in a single operation.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Desktop & Web */}
        <section className="bg-white border-b border-[#eee]">
          <div className="max-w-[1200px] mx-auto px-6 py-16 lg:py-20">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
              <div>
                <h2 className="text-[24px] sm:text-[28px] font-semibold text-[#1a1a1a] leading-tight mb-4">
                  Native desktop applications
                </h2>
                <p className="text-[16px] text-[#555] leading-[1.65] mb-4">
                  TekSpert Mail is available as a native desktop application for both macOS and Windows. The desktop apps are built using Electron and share the same codebase as the web client, so the interface and functionality are identical across platforms.
                </p>
                <p className="text-[16px] text-[#555] leading-[1.65] mb-4">
                  Desktop apps provide native system notifications when new emails arrive, faster startup times compared to loading a browser tab, and a dedicated window that stays in your taskbar or dock for easy access.
                </p>
                <p className="text-[16px] text-[#555] leading-[1.65]">
                  <Link href="/download" className="text-[#0078D4] font-semibold hover:underline">Download the desktop app →</Link>
                </p>
              </div>
              <div>
                <h2 className="text-[24px] sm:text-[28px] font-semibold text-[#1a1a1a] leading-tight mb-4">
                  Full-featured web client
                </h2>
                <p className="text-[16px] text-[#555] leading-[1.65] mb-4">
                  Access your email from any modern web browser without installing anything. The web client provides the full TekSpert Mail experience — including the three-column layout, search, compose, account switching, and all settings.
                </p>
                <p className="text-[16px] text-[#555] leading-[1.65]">
                  The web client is responsive and works on tablets and mobile browsers. It supports both light and dark themes, which can be toggled from the header.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Sync & Reliability */}
        <section className="bg-[#fafafa] border-b border-[#eee]">
          <div className="max-w-[1200px] mx-auto px-6 py-16 lg:py-20">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
              <div>
                <h2 className="text-[24px] sm:text-[28px] font-semibold text-[#1a1a1a] leading-tight mb-4">
                  Real-time email synchronisation
                </h2>
                <p className="text-[16px] text-[#555] leading-[1.65] mb-4">
                  The TekSpert Mail sync engine keeps your mailbox up to date across all of your devices. New messages, read status changes, and folder modifications are reflected within seconds whether you are using the web client or a desktop app.
                </p>
                <p className="text-[16px] text-[#555] leading-[1.65]">
                  A background worker process runs continuously to process incoming mail across all accounts hosted on our server. You can also trigger a manual sync at any time from the toolbar refresh button.
                </p>
              </div>
              <div>
                <h2 className="text-[24px] sm:text-[28px] font-semibold text-[#1a1a1a] leading-tight mb-4">
                  Contact management
                </h2>
                <p className="text-[16px] text-[#555] leading-[1.65] mb-4">
                  TekSpert Mail automatically builds a contact list from the people you correspond with. Contacts are displayed with their name, email address, and a colour-coded avatar.
                </p>
                <p className="text-[16px] text-[#555] leading-[1.65]">
                  When composing a new email, the recipient field provides auto-complete suggestions from your contact list so you can address messages quickly without typing the full email address.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-[#0078D4]">
          <div className="max-w-[800px] mx-auto px-6 py-14 text-center">
            <h2 className="text-[24px] sm:text-[28px] font-semibold text-white leading-tight mb-4">
              Ready to try TekSpert Mail?
            </h2>
            <p className="text-[16px] text-white/80 leading-[1.65] mb-6">
              Create a free account and start using all of these features today.
            </p>
            <Link
              href="/auth/register"
              className="inline-flex px-6 py-3 bg-white text-[#0078D4] text-[15px] font-semibold rounded-[4px] hover:bg-[#f5f5f5] transition-colors"
            >
              Create free account
            </Link>
          </div>
        </section>
      </main>

      <MarketingFooter />
    </>
  );
}
